import { useCallback } from 'react';
import { usePermissions } from '../contexts/PermissionContext';

/**
 * Lab features a super admin switches per clinic on Clinic Access -> Modules (medopd-docs 152). Each is a module
 * whose parent is LAB; the server sends it in enabledModules while it is on.
 */
export const LAB_FEATURES = {
  frontDesk: 'LAB_FRONT_DESK',
  referrers: 'LAB_REFERRERS',
  priceLists: 'LAB_PRICE_LISTS',
  analytics: 'LAB_ANALYTICS',
  inventory: 'LAB_INVENTORY',
  qc: 'LAB_QC',
  calculations: 'LAB_CALCULATIONS',
  deltaChecks: 'LAB_DELTA_CHECKS',
  reportDelivery: 'LAB_REPORT_DELIVERY',
  twoLevelSignoff: 'LAB_TWO_LEVEL_SIGNOFF',
} as const;

export type LabFeature = (typeof LAB_FEATURES)[keyof typeof LAB_FEATURES];

/**
 * `labFeatureOn(code)`. Until the clinic's modules have loaded, a feature counts as on: the screen only hides what is
 * known to be off, and the server refuses a switched-off feature either way.
 */
export function useLabFeatures() {
  const { moduleEnabled, enabledModules, isLoading } = usePermissions();
  const known = !isLoading && (enabledModules?.size ?? 0) > 0;
  return useCallback((feature: LabFeature) => !known || moduleEnabled(feature), [known, moduleEnabled]);
}

/**
 * `labFeatureConfirmed(code)`: true only once the clinic's modules have loaded and the feature is among them. Use it to
 * gate a background request (a polling query) to a feature-switched endpoint: the server answers 403 while the feature
 * is off, so the request must not start on a guess.
 */
export function useLabFeatureConfirmed() {
  const { moduleEnabled, isLoading } = usePermissions();
  return useCallback((feature: LabFeature) => !isLoading && moduleEnabled(feature), [isLoading, moduleEnabled]);
}
